import SideMenu from "../atoms/SideMenu.tsx";
import {FC} from "react";
import {Column, Row} from "../atoms/Layout.tsx";
import {CloseCircleIcon} from "../atoms/Icon";
import PillSelectGroup from "./PillSelectGroup.tsx";
import {Option} from "../atoms/SelectInput.tsx";
import {CATEGORIES, Issue, PRIORITIES} from "../api/issue.ts";
import SelectInputGroup from "./SelectInputGroup.tsx";

export type IssueFilterDateRange = 'all' | 'thisMonth' | 'thisWeek' | 'today' | 'lastHour';

export interface IssueFilters {
    priorityIds: string[];
    dateRange: IssueFilterDateRange;
    categoryId?: string;
    location?: string;
}

export const DEFAULT_ISSUE_FILTERS: IssueFilters = {
    priorityIds: [],
    dateRange: 'all',
    categoryId: undefined,
    location: undefined,
}

export const doesIssueMatchFilter = (issue: Issue, filters: IssueFilters): boolean => {
    if (filters.priorityIds.length > 0 && !filters.priorityIds.includes(issue.priority.id)) {
        return false;
    }

    if (filters.dateRange !== 'all') {
        const now = new Date();
        const issueDate = new Date(issue.createdAt);
        switch (filters.dateRange) {
            case 'thisMonth':
                if (issueDate.getMonth() !== now.getMonth() || issueDate.getFullYear() !== now.getFullYear()) {
                    return false;
                }
                break;
            case 'thisWeek':
                const startOfWeek = new Date(now);
                startOfWeek.setDate(now.getDate() - now.getDay());
                if (issueDate < startOfWeek) {
                    return false;
                }
                break;
            case 'today':
                if (issueDate.toDateString() !== now.toDateString()) {
                    return false;
                }
                break;
            case 'lastHour':
                const lastHour = new Date(now);
                lastHour.setHours(now.getHours() - 1);
                if (issueDate < lastHour) {
                    return false;
                }
                break;
            default:
                break;
        }
    }

    if (filters.categoryId && issue.category.id !== filters.categoryId) {
        return false;
    }

    if (filters.location && issue.location !== filters.location) {
        return false;
    }

    return true;
}

const PRIORITY_OPTIONS: Option[] = PRIORITIES.map((priority) => ({
    value: priority.id,
    label: priority.name
}));

const DATE_OPTIONS: Option[] = [
    { value: 'all', label: 'Anytime' },
    { value: 'thisMonth', label: 'This month' },
    { value: 'thisWeek', label: 'This week' },
    { value: 'today', label: 'Today' },
    { value: 'lastHour', label: 'Last hour' }
]

const CATEGORY_OPTIONS: Option[] = [ { value: '', label: 'Any'}, ...CATEGORIES.map((category) => ({
    value: category.id,
    label: category.name
}))];

interface Props {
    open: boolean;
    filters: IssueFilters;
    onChange: (filters: IssueFilters) => void;
    onClose: () => void;
    locations: string[];
}

const IssueFilterMenu: FC<Props> = ({open, filters, onChange, onClose, locations }) => {
    const locationOptions = [{ value: '', label: 'Any' }, ...locations.map(location => ({ value: location, label: location }))];

    return <SideMenu open={open}>
        <Column gap="48">
            <Row>
                <CloseCircleIcon size={24} onClick={onClose} />
            </Row>
            <PillSelectGroup options={PRIORITY_OPTIONS} label="Priority" value={filters.priorityIds} onChange={priorityIds => onChange({...filters, priorityIds})} />
            <SelectInputGroup label="Date added" options={DATE_OPTIONS} value={filters.dateRange} onChange={dateRange => onChange({...filters, dateRange: dateRange as IssueFilterDateRange})} />
            <SelectInputGroup label="Issue type" options={CATEGORY_OPTIONS} value={filters.categoryId || ''} onChange={categoryId => onChange({...filters, categoryId: categoryId || undefined})} />
            <SelectInputGroup label="Location" options={locationOptions} value={filters.location || ''} onChange={location => onChange({...filters, location: location || undefined})} />
        </Column>
    </SideMenu>
}

export default IssueFilterMenu;